import { useState } from 'react';

const ContactSection = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch('https://api.blackdogsettlement.com/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
      form.reset();
    } catch {
      // silent fail
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="bg-[#0f0f0f]" id="contact">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <div className="flex flex-col lg:flex-row gap-16">
          {/* Left */}
          <div className="w-full lg:w-[40%]">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-px bg-[#c9a84c]"></div>
              <span className="text-[#c9a84c] text-xs font-bold tracking-[0.3em] uppercase">Get In Touch</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white leading-tight mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Let's Start Your<br />Settlement Today
            </h2>
            <p className="text-white/40 text-sm leading-relaxed mb-12">
              Ready to close? Have questions about title insurance or the settlement process in Bucks, Montgomery, Chester, or Delaware County? We're here to help.
            </p>

            <div className="space-y-6">
              <a href="tel:2154990796" className="flex items-center gap-5 group cursor-pointer">
                <div className="w-12 h-12 flex items-center justify-center border border-white/10 group-hover:border-[#c9a84c] transition-colors shrink-0">
                  <i className="ri-phone-line text-[#c9a84c] text-lg"></i>
                </div>
                <div>
                  <p className="text-white/30 text-xs uppercase tracking-widest mb-0.5">Phone</p>
                  <p className="text-white font-bold text-sm">215-499-0796</p>
                </div>
              </a>
              <a href="mailto:info@blackdogsettlement.com" className="flex items-center gap-5 group cursor-pointer">
                <div className="w-12 h-12 flex items-center justify-center border border-white/10 group-hover:border-[#c9a84c] transition-colors shrink-0">
                  <i className="ri-mail-line text-[#c9a84c] text-lg"></i>
                </div>
                <div>
                  <p className="text-white/30 text-xs uppercase tracking-widest mb-0.5">Email</p>
                  <p className="text-white font-bold text-sm">info@blackdogsettlement.com</p>
                </div>
              </a>
              <div className="flex items-center gap-5">
                <div className="w-12 h-12 flex items-center justify-center border border-white/10 shrink-0">
                  <i className="ri-map-pin-2-line text-[#c9a84c] text-lg"></i>
                </div>
                <div>
                  <p className="text-white/30 text-xs uppercase tracking-widest mb-0.5">Office</p>
                  <p className="text-white font-bold text-sm">842 Durham Rd, Newtown, PA 18940</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Form */}
          <div className="w-full lg:w-[60%]">
            {submitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-20 border border-white/10">
                <div className="w-16 h-16 flex items-center justify-center border border-[#c9a84c] mb-6">
                  <i className="ri-check-line text-[#c9a84c] text-2xl"></i>
                </div>
                <h3 className="text-white text-2xl font-bold mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Message Received</h3>
                <p className="text-white/40 text-sm">We'll be in touch shortly. You can also reach us at 215-499-0796.</p>
              </div>
            ) : (
              <form
                data-contact-form
                id="contact-form"
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-white/30 text-xs uppercase tracking-widest mb-2">Full Name *</label>
                    <input
                      name="name"
                      type="text"
                      required
                      placeholder="Jane Smith"
                      className="w-full bg-white/5 border border-white/10 text-white placeholder-white/20 px-4 py-3.5 text-sm focus:outline-none focus:border-[#c9a84c] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-white/30 text-xs uppercase tracking-widest mb-2">Phone</label>
                    <input
                      name="phone"
                      type="tel"
                      placeholder="(215) 555-0000"
                      className="w-full bg-white/5 border border-white/10 text-white placeholder-white/20 px-4 py-3.5 text-sm focus:outline-none focus:border-[#c9a84c] transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-white/30 text-xs uppercase tracking-widest mb-2">Email Address *</label>
                  <input
                    name="email"
                    type="email"
                    required
                    placeholder="jane@example.com"
                    className="w-full bg-white/5 border border-white/10 text-white placeholder-white/20 px-4 py-3.5 text-sm focus:outline-none focus:border-[#c9a84c] transition-colors"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-white/30 text-xs uppercase tracking-widest mb-2">County</label>
                    <select
                      name="county"
                      className="w-full bg-white/5 border border-white/10 text-white px-4 py-3.5 text-sm focus:outline-none focus:border-[#c9a84c] transition-colors cursor-pointer"
                    >
                      <option value="" className="bg-[#0f0f0f]">Select County</option>
                      <option className="bg-[#0f0f0f]">Bucks County</option>
                      <option className="bg-[#0f0f0f]">Montgomery County</option>
                      <option className="bg-[#0f0f0f]">Chester County</option>
                      <option className="bg-[#0f0f0f]">Delaware County</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-white/30 text-xs uppercase tracking-widest mb-2">Service Needed</label>
                    <select
                      name="service"
                      className="w-full bg-white/5 border border-white/10 text-white px-4 py-3.5 text-sm focus:outline-none focus:border-[#c9a84c] transition-colors cursor-pointer"
                    >
                      <option value="" className="bg-[#0f0f0f]">Select Service</option>
                      <option className="bg-[#0f0f0f]">Residential Settlement</option>
                      <option className="bg-[#0f0f0f]">Commercial Closing</option>
                      <option className="bg-[#0f0f0f]">Title Insurance</option>
                      <option className="bg-[#0f0f0f]">Title Search</option>
                      <option className="bg-[#0f0f0f]">1031 Exchange</option>
                      <option className="bg-[#0f0f0f]">Refinancing</option>
                      <option className="bg-[#0f0f0f]">Deed Preparation</option>
                      <option className="bg-[#0f0f0f]">Lien Search</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-white/30 text-xs uppercase tracking-widest mb-2">Message</label>
                  <textarea
                    name="message"
                    rows={4}
                    maxLength={500}
                    placeholder="Tell us about your transaction..."
                    className="w-full bg-white/5 border border-white/10 text-white placeholder-white/20 px-4 py-3.5 text-sm focus:outline-none focus:border-[#c9a84c] transition-colors resize-none"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#c9a84c] hover:bg-[#b8963e] disabled:opacity-50 text-[#0f0f0f] font-bold py-4 text-xs tracking-[0.2em] uppercase whitespace-nowrap cursor-pointer transition-colors duration-200"
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
